import { Injectable, Inject } from '@angular/core';
import { StorageService } from './storage.service';
import { LocationStrategy } from '@angular/common';
import { Jwt } from '../web-tokens/jwt';
import { Router } from '@angular/router';
import {
  SecurityConfig,
  SecurityConfigInjectionToken
} from '../configuration/security.config';

/**
 * Provides a mechanisms to determine, store and retrieve redirect paths
 */
@Injectable({ providedIn: 'root' })
export class RedirectService {
  constructor(
    private storageService: StorageService,
    private locationStrategy: LocationStrategy,
    private router: Router,
    @Inject(SecurityConfigInjectionToken) private securityConfig: SecurityConfig
  ) {}

  /**
   * Saves the specified redirect path into whichever storage service is provided
   * @param url The url to save or null to auto-retrieve
   */
  public saveRedirectPath(url?: string | null): void {
    if (!url) {
      url = this.currentPath;
    }

    this.storageService.write('redirect', url);
  }

  /**
   * Retrieves the stored redirect path if applicable otherwise null or undefined
   */
  public readRedirectPath(): string | undefined | null {
    return this.storageService.read('redirect');
  }

  /**
   * Redirects the user to the stored redirect path if applicable
   */
  public navigateToRedirect(): void {
    const redirectPath = this.readRedirectPath();

    if (redirectPath !== undefined && redirectPath !== null) {
      // We need to delete the cookie as the silent-renew will continue to trigger this
      this.deleteRedirectPath();
      // We need to delay the navigation as angular will attempt to
      // put us in the redirect location not sure how this differs
      // from the auto-login redirect, but it was not working
      setTimeout(() => {
        this.router.navigateByUrl(redirectPath);
      }, 0);
    }
  }

  /**
   * Redirects the user to the forbidden route
   * @param overrideRoute A route to use instead of the default forbidden route
   */
  public navigateToForbidden(overrideRoute?: string): void {
    let redirectRoute = this.securityConfig.configuration.routes.forbiddenRoute;

    if (overrideRoute) {
      redirectRoute = overrideRoute;
    }

    if (!redirectRoute) {
      redirectRoute = '/notauthorized';
    }

    this.router.navigate([redirectRoute]);
  }

  /**
   * Removes the redirect path from storage
   */
  public deleteRedirectPath(): void {
    this.storageService.remove('redirect');
  }

  /**
   * Gets the current location path that is safe for redirection
   */
  public get currentPath(): string {
    let hashedEntryPoint = this.locationStrategy.path(true);
    let redirectPath: string;

    if (Jwt.hasTokens(hashedEntryPoint)) {
      redirectPath = this.locationStrategy.path(false);
    } else {
      redirectPath = hashedEntryPoint;
    }

    return redirectPath;
  }

  /**
   * Gets the required query param from the route URL
   */
  public getQueryParam(param: string): string | null | undefined {
    const url = new URL(window.location.href);
    if (url.searchParams.get(param)) {
      return url.searchParams.get(param);
    }
    const searchParams = new URLSearchParams(this.currentPath?.split('?')?.[1]);
    return searchParams?.get(param);
  }
}
